import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import {
  Truck, ClipboardCheck, Eye, Search, Link as LinkIcon,
  Users, Layers, BarChart3, Mail, FileDown, Trash2,
  CheckCircle2, Circle, Clock, Loader2, RefreshCw, ExternalLink
} from "lucide-react";
import { useState, useDeferredValue, memo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// --- Subcomponentes (Definidos antes para evitar problemas de hoisting con memo) ---

const ActionButton = ({ icon: Icon, label, onClick, primary }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2 border-2 text-[10px] font-black tracking-widest uppercase transition-all ${
      primary
        ? "bg-navy text-white border-navy hover:bg-navy-hover shadow-lg shadow-navy/20"
        : "bg-white border-border text-navy hover:border-navy hover:bg-navy/5"
    }`}
  >
    <Icon size={14} />
    {label}
  </button>
);

const AdminTool = ({ icon: Icon, label, onClick, variant = "default" }) => {
  const colors = {
    default: "bg-white border-navy/20 text-navy hover:bg-navy hover:text-white",
    warn: "bg-warn/10 border-warn/30 text-warn-foreground hover:bg-warn hover:text-white",
    navy: "bg-navy text-white hover:bg-navy-hover"
  };

  return (
    <button
      onClick={onClick}
      className={`flex items-center justify-center gap-2 p-3 border-2 transition-all font-bold text-[11px] uppercase tracking-wider ${colors[variant]}`}
    >
      <Icon size={16} />
      {label}
    </button>
  );
};

const TabButton = ({ active, icon: Icon, label, onClick }) => (
  <button
    onClick={onClick}
    className={`flex-1 flex items-center justify-center gap-3 py-5 font-black text-sm tracking-widest transition-all ${
      active
        ? "bg-navy text-white border-b-4 border-warn"
        : "text-muted-foreground hover:bg-navy/5"
    }`}
  >
    <Icon size={20} />
    {label}
  </button>
);

const RecordCard = memo(function RecordCard({ record, isAdmin, onDelete }) {
  const navigate = useNavigate();
  const [downloading, setDownloading] = useState(false);
  const e = record.entry || {};

  const handleDownloadPDF = async () => {
    try {
      setDownloading(true);
      const res = await api.get(`/siruc/records/${record.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `REPORTE_CONSOLIDADO_${record.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      toast.error("Error al descargar reporte");
    } finally {
      setDownloading(false);
    }
  };

  const steps = [
    { label: "Entrada", done: !!record.entry },
    { label: "Inspección", done: record.inspection_ids?.length > 0 || record.status === "inspeccionado" },
    { label: "Embarque", done: !!record.shipping_ticket_id },
    { label: "Salida", done: record.status === "salida" }
  ];

  return (
    <div className="bg-white border-2 border-border hover:border-navy transition-all p-4 group">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-3">
            <h3 className="text-xl font-black text-navy tracking-tight">{e.placas_unidad || "S/P"}</h3>
            <span className="text-xs font-bold text-muted-foreground uppercase">{e.tipo_unidad}</span>
          </div>
          <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
            {e.chofer_nombre} <span className="mx-2">·</span> {e.compania_transporte || "Sin Compañía"}
          </p>

          <div className="flex items-center gap-2 pt-2">
            {steps.map((s, i) => (
              <div key={i} className="flex items-center gap-2">
                {s.done ? (
                  <CheckCircle2 size={20} className="text-success" />
                ) : (
                  <Circle size={20} className="text-border" />
                )}
                {i < steps.length - 1 && <div className={`w-4 h-0.5 ${steps[i+1].done ? 'bg-success' : 'bg-border'}`} />}
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col items-end gap-2">
          <div className="flex gap-2">
            {record.status === "salida" ? (
              <span className="bg-success text-white px-3 py-1 text-[10px] font-black uppercase tracking-tighter">SALIÓ</span>
            ) : (
              <span className="bg-navy text-white px-3 py-1 text-[10px] font-black uppercase tracking-tighter">EN PLANTA</span>
            )}
            {record.inspection_ids?.length > 0 && (
              <span className="bg-success/20 text-success border border-success/30 px-3 py-1 text-[10px] font-black uppercase tracking-tighter">INSP. COMPLETA</span>
            )}
          </div>

          <div className="flex items-center gap-4 mt-2">
            {isAdmin && (
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(); }}
                className="text-critical/40 hover:text-critical p-1 transition-colors"
              >
                <Trash2 size={16} />
              </button>
            )}
            <p className="text-[10px] font-bold text-muted-foreground">
              {new Date(record.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-dashed border-border opacity-0 group-hover:opacity-100 transition-opacity">
        <ActionButton icon={Eye} label="VER DETALLE" onClick={() => navigate(`/siruc/records/${record.id}`)} />
        {record.status === "salida" && (
          <ActionButton
            icon={FileDown}
            label={downloading ? "GENERANDO..." : "REPORTE CONSOLIDADO"}
            onClick={handleDownloadPDF}
            primary
          />
        )}
        {record.drive_report_link && (
          <ActionButton
            icon={ExternalLink}
            label="VER EN DRIVE"
            onClick={() => window.open(record.drive_report_link, '_blank')}
          />
        )}
        <ActionButton icon={Mail} label="ENVIAR CORREO" />
        <ActionButton icon={Truck} label="GESTIONAR / EDITAR" onClick={() => navigate(`/siruc/records/${record.id}`)} />
        {isAdmin && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (confirm("¿Estás seguro de eliminar este registro maestro por completo?")) {
                onDelete();
              }
            }}
            className="flex items-center gap-2 px-4 py-2 border-2 border-critical/20 text-critical text-[10px] font-black tracking-widest uppercase hover:bg-critical hover:text-white transition-all ml-auto"
          >
            <Trash2 size={14} /> ELIMINAR REGISTRO
          </button>
        )}
      </div>
    </div>
  );
});

// --- Componente Principal ---

export default function MasterPanel() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("caseta");
  const deferredSearch = useDeferredValue(search);

  const isAdmin = user?.role === "admin";

  const { data: records = [], isLoading, isFetching } = useQuery({
    queryKey: ["master-records", deferredSearch, activeTab],
    queryFn: () => api.get("/siruc/records", {
      params: { search: deferredSearch, tab: activeTab }
    }).then((r) => r.data),
    staleTime: 30000,
    gcTime: 10 * 60 * 1000,
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/siruc/records/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["master-records"] });
      toast.success("Registro eliminado");
    },
    onError: (e) => toast.error(apiError(e, "Error al eliminar")),
  });

  return (
    <div className="p-4 lg:p-6 max-w-7xl mx-auto space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] font-bold text-navy bg-navy/5 px-2 py-0.5 rounded tracking-widest uppercase">Yard Management</span>
          </div>
          <h1 className="font-heading text-4xl font-black tracking-tighter text-navy uppercase italic">NAF <span className="text-muted-foreground font-light not-italic text-2xl ml-2">Panel</span></h1>
        </div>
        {isFetching && !isLoading && <Loader2 className="animate-spin text-navy/20" size={20} />}
      </header>

      {isAdmin && (
        <section className="bg-navy/5 border-2 border-navy/20 p-4">
          <p className="text-[10px] font-black tracking-[0.2em] text-navy mb-4 uppercase">Herramientas de Administración</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <AdminTool icon={LinkIcon} label="Vincular Registros Huérfanos" />
            <AdminTool
              icon={FileDown}
              label="Carpeta Nube (PDFs)"
              variant="navy"
              onClick={async () => {
                const res = await api.get("/config/drive-folder");
                if (res.data.link) window.open(res.data.link, '_blank');
              }}
            />
            <AdminTool
              icon={RefreshCw}
              label="Jalar Reportes SRIUC"
              variant="warn"
              onClick={async () => {
                toast.info("Iniciando descarga masiva de reportes desde SRIUC...");
                await api.post("/sync/sriuc/pdfs");
                toast.success("Sincronización iniciada en segundo plano. Los reportes aparecerán en Drive pronto.");
              }}
            />
            <AdminTool icon={Users} label="Usuarios" onClick={() => navigate("/users")} />
            <AdminTool icon={BarChart3} label="KPIs / Analítica" />
          </div>
        </section>
      )}

      <div className="flex border-b-4 border-navy/10 bg-white">
        <TabButton active={activeTab === "caseta"} onClick={() => setActiveTab("caseta")} icon={Truck} label="CASETA" />
        <TabButton active={activeTab === "inspeccion"} onClick={() => setActiveTab("inspeccion")} icon={ClipboardCheck} label="INSPECCIÓN" />
        <TabButton active={activeTab === "embarque"} onClick={() => setActiveTab("embarque")} icon={Layers} label="EMBARQUE" />
      </div>

      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={20} />
        <input
          type="text"
          placeholder="Buscar por placas, chofer o compañía..."
          className="w-full h-14 pl-12 bg-white border-2 border-border focus:border-navy outline-none text-lg font-medium transition-all"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="space-y-3">
        {isLoading ? (
          <div className="py-20 text-center animate-pulse text-navy font-bold flex flex-col items-center gap-3">
            <Loader2 className="animate-spin" />
            Cargando registros maestros...
          </div>
        ) : records.length === 0 ? (
          <div className="py-20 text-center border-2 border-dashed border-border text-muted-foreground">
            No se encontraron registros activos en esta categoría.
          </div>
        ) : (
          records.map((r) => (
            <RecordCard key={r.id} record={r} isAdmin={isAdmin} onDelete={() => deleteMutation.mutate(r.id)} />
          ))
        )}
      </div>
    </div>
  );
}
